import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Download, KeyRound, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem("fitnance_admin_token") || "");
  const [passcode, setPasscode] = useState("");
  const [entries, setEntries] = useState(null);
  const [messages, setMessages] = useState(null);
  const [error, setError] = useState("");
  const [authError, setAuthError] = useState("");
  const [checking, setChecking] = useState(false);

  const login = async (e) => {
    e.preventDefault();
    setChecking(true);
    setAuthError("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { passcode });
      sessionStorage.setItem("fitnance_admin_token", data.token);
      setToken(data.token);
      setPasscode("");
    } catch {
      setAuthError("Wrong passcode — try again.");
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    axios
      .get(`${API}/waitlist`, { headers })
      .then(({ data }) => setEntries(data))
      .catch((err) => {
        if (err.response?.status === 401) {
          sessionStorage.removeItem("fitnance_admin_token");
          setToken("");
        } else {
          setError("Could not load the waitlist.");
        }
      });
    axios
      .get(`${API}/support`, { headers })
      .then(({ data }) => setMessages(data))
      .catch(() => setMessages([]));
  }, [token]);

  const exportCsv = () => {
    const rows = [["email", "joined_at"], ...entries.map((e) => [e.email, e.created_at])];
    const csv = rows.map((r) => r.join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "fitnance-waitlist.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!token) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-[#08090C] px-4 text-slate-50">
        <form onSubmit={login} className="w-full max-w-sm rounded-3xl border border-white/10 bg-[#111318] p-8">
          <div className="mb-6 flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-500/10">
              <KeyRound className="h-5 w-5 text-emerald-400" />
            </span>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">Restricted</p>
              <h1 className="text-lg font-bold tracking-tight">Admin access</h1>
            </div>
          </div>
          <input
            data-testid="admin-passcode-input"
            type="password"
            value={passcode}
            onChange={(e) => setPasscode(e.target.value)}
            placeholder="Passcode"
            autoFocus
            className="h-12 w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 text-sm text-slate-100 placeholder:text-neutral-600 outline-none transition-colors duration-300 focus:border-emerald-500/50"
          />
          {authError && (
            <p data-testid="admin-passcode-error" className="mt-2 text-xs text-red-400">{authError}</p>
          )}
          <button
            data-testid="admin-unlock-button"
            type="submit"
            disabled={checking}
            className="mt-4 flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-emerald-500 font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-emerald-950 transition-colors duration-300 hover:bg-emerald-400 disabled:opacity-60"
          >
            {checking ? <Loader2 className="h-4 w-4 animate-spin" /> : "Unlock"}
          </button>
          <Link to="/" className="mt-4 block text-center font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-600 transition-colors hover:text-emerald-400">
            Back to site
          </Link>
        </form>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#08090C] px-4 py-16 text-slate-50 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-3xl">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.3em] text-emerald-400/90">
              Fitnance · Admin
            </p>
            <h1 className="mt-2 text-3xl font-bold tracking-tight">Waitlist signups</h1>
          </div>
          <div className="flex items-center gap-3">
            {entries && entries.length > 0 && (
              <button
                data-testid="admin-export-csv-button"
                onClick={exportCsv}
                className="flex items-center gap-2 rounded-full border border-white/10 px-5 py-2.5 font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-neutral-300 transition-colors duration-300 hover:border-emerald-500/40 hover:text-emerald-400"
              >
                <Download className="h-3.5 w-3.5" />
                Export CSV
              </button>
            )}
            <Link
              to="/"
              data-testid="admin-modal-close-button"
              className="flex items-center gap-2 rounded-full bg-emerald-500 px-5 py-2.5 font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-emerald-950 transition-colors duration-300 hover:bg-emerald-400"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Back to site
            </Link>
          </div>
        </div>

        <div className="mt-10 overflow-hidden rounded-3xl border border-white/10 bg-[#111318]">
          {error && <p className="p-8 text-sm text-red-400">{error}</p>}
          {!entries && !error && (
            <p className="flex items-center gap-3 p-8 text-sm text-neutral-400">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading signups…
            </p>
          )}
          {entries && entries.length === 0 && (
            <p data-testid="admin-empty-state" className="p-8 text-sm text-neutral-400">
              No signups yet — share the page and watch this fill up.
            </p>
          )}
          {entries && entries.length > 0 && (
            <table data-testid="admin-waitlist-table" className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-white/5 font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                  <th className="px-6 py-4 font-medium">#</th>
                  <th className="px-6 py-4 font-medium">Email</th>
                  <th className="px-6 py-4 font-medium">Joined</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e, i) => (
                  <tr key={e.id} className="border-b border-white/5 last:border-0 hover:bg-white/[0.02]">
                    <td className="px-6 py-4 font-mono text-xs text-neutral-500">{entries.length - i}</td>
                    <td className="px-6 py-4 text-slate-100">{e.email}</td>
                    <td className="px-6 py-4 text-neutral-400">
                      {new Date(e.created_at).toLocaleDateString(undefined, {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {entries && (
          <p data-testid="admin-total-count" className="mt-6 font-mono text-[11px] uppercase tracking-[0.25em] text-neutral-500">
            Total: {entries.length} {entries.length === 1 ? "signup" : "signups"}
          </p>
        )}

        <h2 className="mt-16 text-xl font-bold tracking-tight">Support messages</h2>
        <div className="mt-6 overflow-hidden rounded-3xl border border-white/10 bg-[#111318]">
          {messages === null && (
            <p className="flex items-center gap-3 p-8 text-sm text-neutral-400">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading messages…
            </p>
          )}
          {messages && messages.length === 0 && (
            <p data-testid="admin-support-empty" className="p-8 text-sm text-neutral-400">
              No support messages yet.
            </p>
          )}
          {messages && messages.length > 0 && (
            <table data-testid="admin-support-table" className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-white/5 font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                  <th className="px-6 py-4 font-medium">Email</th>
                  <th className="px-6 py-4 font-medium">Message</th>
                  <th className="px-6 py-4 font-medium">Received</th>
                </tr>
              </thead>
              <tbody>
                {messages.map((m) => (
                  <tr key={m.id} className="border-b border-white/5 align-top last:border-0 hover:bg-white/[0.02]">
                    <td className="whitespace-nowrap px-6 py-4 text-slate-100">{m.email}</td>
                    <td className="max-w-md px-6 py-4 text-neutral-300">{m.message}</td>
                    <td className="whitespace-nowrap px-6 py-4 text-neutral-400">
                      {new Date(m.created_at).toLocaleDateString(undefined, {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </main>
  );
}
